import { catchError, DefaultOptionType, search } from "@opendash/core";
import { useTranslation } from "@opendash/i18n";
import { Form, Input, Select } from "antd";
import { Rule } from "antd/es/form";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { useEffect, useMemo, useState } from "react";
import { StepFieldType } from "..";
import { StepValueType } from "../types/StepTypes";

type Props = {
  field: StepFieldType;
  onChange?: (value: StepValueType | StepValueType[]) => void;
};

export default observer((props: Props) => {
  const t = useTranslation();
  const [rules, setRules] = useState<Rule[]>([]);
  /**
   * @type {DefaultOptionType[]}
   * {label: string, value: string}
   */
  const [allOptions, setAllOptions] = useState<DefaultOptionType[]>([]);
  // const [filteredOptions, setFilteredOptions] = useState<DefaultOptionType[]>([]);
  const [linkedOptions, setLinkedOptions] = useState<DefaultOptionType[]>([]);
  const [searchString, setSearchString] = useState<string>("");

  useEffect(() => {
    const rule: Rule = {
      required: props.field.metafield.required,
      validator(rule, value) {
        if (
          (!value || (Array.isArray(value) && value.length <= 0)) &&
          rule.required
        ) {
          return Promise.reject(
            t("maintenance:schedule.meta.input.error.required", {
              field: value.metafield.label,
            })
          );
        } else {
          return Promise.resolve();
        }
      },
    };

    const options: DefaultOptionType[] = [
      ...props.field.metafield.allowedValues
        .filter((value) => !value.startsWith("@"))
        .map((value) => ({
          label: value,
          value,
        })),
      ...linkedOptions,
    ];

    setAllOptions(options);

    setRules([rule]);
  }, [props.field, linkedOptions]);

  useEffect(() => {
    const fetch = async () => {
      const filteredfields = props.field.metafield.allowedValues.filter(
        (value) => value.startsWith("@")
      );

      for await (const linkedClassname of filteredfields) {
        const classname = linkedClassname.substring(1);
        const [linkedOptionsError, fetchedLinkedOptions] = await catchError(
          new Parse.Query(classname).includeAll().limit(1000).find()
        );
        if (linkedOptionsError || fetchedLinkedOptions === undefined) {
          return;
        }

        setLinkedOptions((prev) => [
          ...prev,
          ...fetchedLinkedOptions.map((option) => ({
            label: t("maintenance:schedule.meta.input.linked." + classname, {
              ...option.attributes,
            }),
            value: option.id.toString(),
            classname: classname,
          })),
        ]);
      }
    };

    fetch();
  }, []);

  const filteredOptions = useMemo(
    () =>
      search(
        searchString === null ? "" : searchString,
        allOptions,
        (row: DefaultOptionType) => {
          return row.label as string;
        }
      ),
    [allOptions.length, searchString]
  );

  return (
    <Form.Item
      label={props.field.metafield.label}
      rules={rules}
      name={["meta", props.field.metafield.label]}
      initialValue={
        Array.isArray(props.field.value)
          ? props.field.value.length > 0
            ? props.field.value
            : undefined
          : props.field.value
      }
    >
      {props.field.metafield.allowedValues.length > 0 && (
        <Select
          filterOption={false}
          onChange={(value, option) => props.onChange(option as StepValueType)}
          options={filteredOptions}
          showSearch
          onSearch={(value) => {
            setSearchString(value);
          }}
          labelInValue
          mode={
            props.field.metafield.onlyOneAllowedValue ? undefined : "multiple"
          }
        />
      )}

      {props.field.metafield.allowedValues.length <= 0 && (
        <Input
          onChange={(event) =>
            props.onChange && event.target.value !== undefined
              ? props.onChange({
                  value: event.target.value,
                  classname: undefined,
                })
              : null
          }
        />
      )}
    </Form.Item>
  );
});
